import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import QRCode from 'qrcode'
import { clienturl } from '../script/variables'
import '../style/TableQRManager.css'

/* ═══════════════════════════════════════════════════════════
   Font options for text layers — all loaded via theme.css or
   the local Google Fonts import at the top of the CSS file.
═══════════════════════════════════════════════════════════ */
const FONT_OPTIONS = [
  { label: 'Poppins — Modern',           value: "'Poppins', sans-serif" },
  { label: 'Playfair Display — Elegant', value: "'Playfair Display', serif" },
  { label: 'Mukta — Clean',              value: "'Mukta', sans-serif" },
  { label: 'DM Sans — Friendly',         value: "'DM Sans', sans-serif" },
  { label: 'DM Mono — Technical',        value: "'DM Mono', monospace" },
  { label: 'Georgia — Classic Serif',    value: "Georgia, serif" },
]

const WEIGHT_OPTIONS = [
  { label: 'Regular',  value: 400 },
  { label: 'Medium',   value: 500 },
  { label: 'Semibold', value: 600 },
  { label: 'Bold',     value: 700 },
]

/* ═══════════════════════════════════════════════════════════
   Print-accurate size templates.
   Print items: mm at 300 DPI (industry standard for table
   cards / standees). Digital items: plain px at 72 DPI.
═══════════════════════════════════════════════════════════ */
const SIZE_TEMPLATES = [
  { id: 'mini-card',    label: 'Mini Table Card',  category: 'Print',   widthMM: 50,  heightMM: 50,  dpi: 300, icon: '◽', note: 'One per table — sits in a stand' },
  { id: 'table-tent',   label: 'Table Tent',       category: 'Print',   widthMM: 100, heightMM: 100, dpi: 300, icon: '▲', note: 'Standard tri-fold table tent insert' },
  { id: 'counter-card', label: 'Counter Card',     category: 'Print',   widthMM: 100, heightMM: 150, dpi: 300, icon: '▤', note: 'Billing counter or host stand' },
  { id: 'standee-a5',   label: 'Standee (A5)',     category: 'Print',   widthMM: 148, heightMM: 210, dpi: 300, icon: '▥', note: 'Entrance or counter standee' },
  { id: 'poster-a4',    label: 'Wall Poster (A4)', category: 'Print',   widthMM: 210, heightMM: 297, dpi: 300, icon: '▦', note: 'Window or wall display' },
  { id: 'ig-story',     label: 'Instagram Story',  category: 'Digital', widthPx: 1080, heightPx: 1920, dpi: 72, icon: '▯', note: 'Share to Instagram / WhatsApp status' },
  { id: 'ig-post',      label: 'Instagram Post',   category: 'Digital', widthPx: 1080, heightPx: 1080, dpi: 72, icon: '▢', note: 'Square social media post' },
]

const MM_PER_INCH = 25.4

/* Reference object for the size-comparison visual — a standard
   1 litre water bottle (e.g. Bisleri). Retail listings vary a
   little (24–28cm tall, 7.5–10cm diameter depending on shape),
   so these are rounded, representative figures — labelled
   "approx." in the UI rather than presented as exact. */
const REFERENCE_BOTTLE = { label: '1L water bottle', heightMM: 245, widthMM: 80 }

function getTemplatePx(t) {
  if (t.widthPx) return { w: t.widthPx, h: t.heightPx }
  return {
    w: Math.round((t.widthMM / MM_PER_INCH) * t.dpi),
    h: Math.round((t.heightMM / MM_PER_INCH) * t.dpi),
  }
}

function formatDims(t) {
  const { w, h } = getTemplatePx(t)
  if (t.widthPx) return `${w} × ${h}px`
  const cm = (t.widthMM / 10).toFixed(1).replace(/\.0$/, '')
  const cmH = (t.heightMM / 10).toFixed(1).replace(/\.0$/, '')
  return `${cm} × ${cmH} cm  ·  ${w} × ${h}px @ ${t.dpi}dpi`
}

/* Plain-language height comparison against the reference bottle,
   e.g. "about as tall as a 1L water bottle" or "2.4× taller than
   a 1L water bottle". */
function compareHeightToBottle(t) {
  if (!t.heightMM) return null
  const ratio = t.heightMM / REFERENCE_BOTTLE.heightMM
  if (ratio < 0.35) return `just over a quarter the height of a ${REFERENCE_BOTTLE.label}`
  if (ratio < 0.65) return `about half the height of a ${REFERENCE_BOTTLE.label}`
  if (ratio < 0.9) return `just under the height of a ${REFERENCE_BOTTLE.label}`
  if (ratio <= 1.12) return `about as tall as a ${REFERENCE_BOTTLE.label}`
  return `${ratio.toFixed(1)}× the height of a ${REFERENCE_BOTTLE.label}`
}

/* ═══════════════════════════════════════════════════════════
   Size comparison visual — draws the reference bottle and the
   selected template's outline to the same scale, side by side,
   so the size is felt rather than just read as numbers.
═══════════════════════════════════════════════════════════ */
const SizeCompare = ({ size }) => {
  if (!size || size.widthPx) return null // only meaningful for physical/print sizes

  const MAX_H = 150 // px, caps the taller of the two shapes
  const tallestMM = Math.max(REFERENCE_BOTTLE.heightMM, size.heightMM)
  const scale = MAX_H / tallestMM

  const bottleH = REFERENCE_BOTTLE.heightMM * scale
  const bottleW = REFERENCE_BOTTLE.widthMM * scale
  const cardH = size.heightMM * scale
  const cardW = size.widthMM * scale

  const neckH = bottleH * 0.12
  const capH = bottleH * 0.06
  const shoulderH = bottleH * 0.08

  return (
    <div className="qrm-compare">
      <div className="qrm-compare-shapes">
        <div className="qrm-compare-item">
          <svg
            width={Math.max(bottleW, 30)}
            height={MAX_H}
            viewBox={`0 0 ${Math.max(bottleW, 30)} ${MAX_H}`}
            className="qrm-compare-svg"
            aria-hidden="true"
          >
            <g transform={`translate(0, ${MAX_H - bottleH})`}>
              {/* cap */}
              <rect x={bottleW * 0.32} y={0} width={bottleW * 0.36} height={capH} rx={2} className="qrm-compare-bottle-cap" />
              {/* neck */}
              <rect x={bottleW * 0.36} y={capH} width={bottleW * 0.28} height={neckH} className="qrm-compare-bottle-body" />
              {/* shoulder + body, rounded */}
              <path
                d={`M ${bottleW * 0.36} ${capH + neckH}
                    C ${bottleW * 0.36} ${capH + neckH}, 0 ${capH + neckH + shoulderH}, 0 ${capH + neckH + shoulderH * 1.6}
                    L 0 ${bottleH - 6}
                    Q 0 ${bottleH} 6 ${bottleH}
                    L ${bottleW - 6} ${bottleH}
                    Q ${bottleW} ${bottleH} ${bottleW} ${bottleH - 6}
                    L ${bottleW} ${capH + neckH + shoulderH * 1.6}
                    C ${bottleW} ${capH + neckH + shoulderH}, ${bottleW * 0.64} ${capH + neckH}, ${bottleW * 0.64} ${capH + neckH}
                    Z`}
                className="qrm-compare-bottle-body"
              />
              <rect
                x={bottleW * 0.1} y={capH + neckH + shoulderH * 2.2}
                width={bottleW * 0.8} height={bottleH * 0.32}
                className="qrm-compare-bottle-label"
              />
            </g>
          </svg>
          <span className="qrm-compare-caption">{REFERENCE_BOTTLE.label}<br />~{(REFERENCE_BOTTLE.heightMM / 10).toFixed(0)}cm tall</span>
        </div>

        <div className="qrm-compare-item">
          <svg
            width={Math.max(cardW, 30)}
            height={MAX_H}
            viewBox={`0 0 ${Math.max(cardW, 30)} ${MAX_H}`}
            className="qrm-compare-svg"
            aria-hidden="true"
          >
            <rect
              x={0} y={MAX_H - cardH}
              width={Math.max(cardW, 30)} height={cardH}
              rx={3}
              className="qrm-compare-card"
            />
          </svg>
          <span className="qrm-compare-caption">{size.label}<br />{(size.widthMM / 10).toFixed(1)}×{(size.heightMM / 10).toFixed(1)}cm</span>
        </div>
      </div>
      <p className="qrm-compare-note">
        📏 {size.label} is {compareHeightToBottle(size)} — placed side by side on a table.
      </p>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Theme Registry — each theme paints a background + border
   treatment. Decorative line-art is drawn in a "virtual 300px"
   coordinate space via withScaledDecor so every theme scales
   cleanly from a 5cm mini card up to an A4 poster. The QR code
   is composited on top. Text layers are NOT drawn here — they
   live as a DOM overlay while editing and are only baked into
   a canvas once, at export time (see renderComposite).
═══════════════════════════════════════════════════════════ */
const SIZE = 300 // virtual coordinate basis for decorative art & text scale

function withScaledDecor(ctx, canvas, drawFn) {
  const scale = Math.min(canvas.width, canvas.height) / SIZE
  ctx.save()
  ctx.scale(scale, scale)
  drawFn(canvas.width / scale, canvas.height / scale)
  ctx.restore()
}

async function drawQr(canvas, qrLink, colors) {
  const ctx = canvas.getContext('2d')
  const minDim = Math.min(canvas.width, canvas.height)
  const qrSize = minDim * 0.56
  const cx = canvas.width / 2
  const cy = canvas.height * 0.42
  const x = cx - qrSize / 2
  const y = cy - qrSize / 2

  const qrCanvas = document.createElement('canvas')
  await QRCode.toCanvas(qrCanvas, qrLink, {
    width: Math.max(220, Math.round(qrSize)),
    margin: 1,
    color: {
      dark: colors.fg === 'transparent' ? '#000000' : colors.fg,
      light: colors.bg === 'transparent' ? '#ffffff00' : colors.bg,
    },
    errorCorrectionLevel: 'H',
  })

  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.14)'
  ctx.shadowBlur = minDim * 0.02
  ctx.shadowOffsetY = minDim * 0.006
  ctx.fillStyle = colors.bg === 'transparent' ? '#ffffff' : colors.bg
  roundRect(ctx, x - minDim * 0.02, y - minDim * 0.02, qrSize + minDim * 0.04, qrSize + minDim * 0.04, minDim * 0.02, ctx.fillStyle)
  ctx.restore()

  ctx.drawImage(qrCanvas, x, y, qrSize, qrSize)
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}
function roundRect(ctx, x, y, w, h, r, fill) {
  roundRectPath(ctx, x, y, w, h, r)
  if (fill) { ctx.fillStyle = fill; ctx.fill() }
}

/* ── Decorative helpers (operate in the virtual 300px space) ── */
function drawOrnateCorners(ctx, w, h, color) {
  ctx.strokeStyle = color; ctx.lineWidth = 1.5
  const s = 20
  ;[[8, 8], [w - 8, 8], [8, h - 8], [w - 8, h - 8]].forEach(([cx, cy], i) => {
    const dx = i % 2 === 0 ? 1 : -1, dy = i < 2 ? 1 : -1
    ctx.beginPath()
    ctx.moveTo(cx, cy + dy * s); ctx.lineTo(cx, cy); ctx.lineTo(cx + dx * s, cy)
    ctx.stroke()
  })
}
function drawDoubleLineBorder(ctx, w, h, color) {
  ctx.strokeStyle = color; ctx.lineWidth = 2.5
  ctx.strokeRect(6, 6, w - 12, h - 12)
  ctx.lineWidth = 0.8
  ctx.strokeRect(11, 11, w - 22, h - 22)
}
function drawGoldBorder(ctx, w, h, color) {
  const grad = ctx.createLinearGradient(0, 0, w, h)
  grad.addColorStop(0, color); grad.addColorStop(0.5, color + 'cc'); grad.addColorStop(1, color)
  ctx.strokeStyle = grad; ctx.lineWidth = 3
  ctx.strokeRect(7, 7, w - 14, h - 14)
  ctx.lineWidth = 0.8; ctx.strokeStyle = color + '88'
  ctx.strokeRect(12, 12, w - 24, h - 24)
  ctx.fillStyle = color
  ;[[7, 7], [w - 7, 7], [7, h - 7], [w - 7, h - 7]].forEach(([x, y]) => {
    ctx.save(); ctx.translate(x, y); ctx.rotate(Math.PI / 4)
    ctx.fillRect(-3, -3, 6, 6); ctx.restore()
  })
}
function drawFleurDeLis(ctx, w, color) {
  ctx.fillStyle = color + '55'; ctx.font = '22px serif'; ctx.textAlign = 'center'
  ctx.fillText('⚜', w / 2, 26)
}
function drawChalkBorder(ctx, w, h, color) {
  ctx.strokeStyle = color + 'cc'; ctx.lineWidth = 3
  ctx.setLineDash([12, 6, 3, 6]); ctx.strokeRect(8, 8, w - 16, h - 16); ctx.setLineDash([])
}
function drawCoffeeRing(ctx, w, h, color) {
  const cx = w / 2, cy = h * 0.3, r = Math.min(w, h) / 2 - 10
  ctx.strokeStyle = color + '55'; ctx.lineWidth = 7
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke()
  ctx.lineWidth = 2.5; ctx.strokeStyle = color + '33'
  ctx.beginPath(); ctx.arc(cx, cy, r - 12, 0, Math.PI * 2); ctx.stroke()
}
function drawCherryBlossoms(ctx, w, h, color) {
  ctx.fillStyle = color + '55'
  const petals = [[15, 15], [w - 15, 15], [15, h - 15], [w - 15, h - 15], [w / 2, 12], [12, h / 2], [w - 12, h / 2]]
  petals.forEach(([x, y]) => {
    for (let a = 0; a < 5; a++) {
      ctx.save(); ctx.translate(x, y); ctx.rotate(a * Math.PI * 2 / 5)
      ctx.beginPath(); ctx.ellipse(0, -5, 3, 5, 0, 0, Math.PI * 2); ctx.fill()
      ctx.restore()
    }
  })
}
function drawItalianStripe(ctx, w, h, color) {
  ctx.fillStyle = color + '22'; ctx.fillRect(0, 0, 10, h); ctx.fillRect(w - 10, 0, 10, h)
  ctx.fillStyle = color + '44'; ctx.fillRect(0, 0, 5, h); ctx.fillRect(w - 5, 0, 5, h)
}
function drawStarfield(ctx, w, h, color) {
  ctx.fillStyle = color
  for (let i = 0; i < 40; i++) {
    const x = Math.random() * w, y = Math.random() * h, r = Math.random() * 1.5 + 0.3
    ctx.globalAlpha = Math.random() * 0.6 + 0.1
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
  }
  ctx.globalAlpha = 1
}
function drawMinimalJapanese(ctx, w, h, color) {
  ctx.strokeStyle = color; ctx.lineWidth = 2
  ctx.beginPath(); ctx.moveTo(0, 18); ctx.lineTo(w, 18); ctx.stroke()
  ctx.lineWidth = 0.8; ctx.strokeStyle = color + '88'
  ctx.beginPath(); ctx.moveTo(0, 22); ctx.lineTo(w, 22); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(0, h - 18); ctx.lineTo(w, h - 18); ctx.stroke()
  ctx.lineWidth = 2; ctx.strokeStyle = color
  ctx.beginPath(); ctx.moveTo(0, h - 22); ctx.lineTo(w, h - 22); ctx.stroke()
}
function drawWoodGrain(ctx, w, h, color) {
  ctx.strokeStyle = color + '33'; ctx.lineWidth = 1
  for (let i = 0; i < 12; i++) {
    ctx.beginPath(); ctx.moveTo(0, i * (h / 12))
    for (let x = 0; x < w; x += 20) ctx.lineTo(x, i * (h / 12) + Math.sin(x / 40 + i) * 3)
    ctx.stroke()
  }
}
function drawPolkaDots(ctx, w, h, color) {
  ctx.fillStyle = color + '44'
  const step = 22
  for (let x = step / 2; x < w; x += step) for (let y = step / 2; y < h; y += step) {
    ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fill()
  }
}
function drawMosaicBorder(ctx, w, h, color) {
  const cols = ['#d4853a', '#2c8b64', '#b5432a', '#c9a84c']
  const sq = 10
  for (let x = 0; x < w; x += sq) {
    ctx.fillStyle = cols[Math.floor(x / sq) % cols.length] + '99'
    ctx.fillRect(x, 0, sq, sq); ctx.fillRect(x, h - sq, sq, sq)
  }
  for (let y = sq; y < h - sq; y += sq) {
    ctx.fillStyle = cols[Math.floor(y / sq) % cols.length] + '99'
    ctx.fillRect(0, y, sq, sq); ctx.fillRect(w - sq, y, sq, sq)
  }
}
function drawSparkles(ctx, w, h, color) {
  ctx.fillStyle = color
  for (let i = 0; i < 25; i++) {
    const x = Math.random() * w, y = Math.random() * h, s = Math.random() * 2 + 1
    ctx.globalAlpha = Math.random() * 0.5 + 0.15
    ctx.fillRect(x - s / 2, y - s / 2, s, s)
  }
  ctx.globalAlpha = 1
}
function drawLeafBorder(ctx, w, h, color) {
  ctx.fillStyle = color + '55'
  const positions = [[10, 10], [w - 10, 10], [10, h - 10], [w - 10, h - 10], [w / 2, 10], [w / 2, h - 10], [10, h / 2], [w - 10, h / 2]]
  positions.forEach(([x, y]) => {
    ctx.save(); ctx.translate(x, y)
    ctx.beginPath(); ctx.ellipse(0, 0, 6, 10, Math.PI / 4, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  })
  ctx.strokeStyle = color + '44'; ctx.lineWidth = 1.5
  ctx.strokeRect(14, 14, w - 28, h - 28)
}
function drawWeaveBorder(ctx, w, h, color) {
  ctx.strokeStyle = color; ctx.lineWidth = 1.5
  const step = 12
  for (let x = 0; x < w; x += step * 2) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + step, 0); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x + step / 2, h); ctx.lineTo(x + step * 1.5, h); ctx.stroke()
  }
  ctx.strokeRect(5, 5, w - 10, h - 10)
}

const QR_THEMES = [
  { id: 'parchment', name: 'Parchment', category: 'Restaurant', emoji: '📜',
    defaultColors: { fg: '#3b2107', bg: '#f5ede0', accent: '#c9a47e' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        ctx.strokeStyle = colors.accent + '22'; ctx.lineWidth = 1
        for (let y = 0; y < h; y += 6) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke() }
        drawOrnateCorners(ctx, w, h, colors.accent)
      })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'bistro', name: 'Le Bistro', category: 'Restaurant', emoji: '🍽️',
    defaultColors: { fg: '#1a0a00', bg: '#fffff8', accent: '#b5432a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => { drawDoubleLineBorder(ctx, w, h, colors.accent); drawFleurDeLis(ctx, w, colors.accent) })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'chalkboard', name: 'Chalkboard', category: 'Restaurant', emoji: '🍺',
    defaultColors: { fg: '#f0ece0', bg: '#2a3830', accent: '#a8c5a0' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawChalkBorder(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'menu_gold', name: 'Menu Gold', category: 'Restaurant', emoji: '✨',
    defaultColors: { fg: '#1b1200', bg: '#faf8f0', accent: '#c9a84c' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawGoldBorder(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'coffee_ring', name: 'Coffee', category: 'Café', emoji: '☕',
    defaultColors: { fg: '#3d1c02', bg: '#fdf6ec', accent: '#8b5e3c' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawCoffeeRing(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'japanese', name: 'Wagashi', category: 'Asian', emoji: '🌸',
    defaultColors: { fg: '#2d1b2e', bg: '#fdf0f5', accent: '#e8799a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        drawCherryBlossoms(ctx, w, h, colors.accent)
        ctx.strokeStyle = colors.accent + '66'; ctx.lineWidth = 1.5
        ctx.strokeRect(8, 8, w - 16, h - 16)
      })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'pizza', name: 'Trattoria', category: 'Italian', emoji: '🍕',
    defaultColors: { fg: '#1e0e00', bg: '#fffaf0', accent: '#c0392b' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawItalianStripe(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'rooftop', name: 'Rooftop Bar', category: 'Bar & Lounge', emoji: '🍹',
    defaultColors: { fg: '#f0e6d3', bg: '#0f1c2e', accent: '#f4a261' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        drawStarfield(ctx, w, h, colors.accent)
        ctx.strokeStyle = colors.accent; ctx.lineWidth = 1.5
        ctx.strokeRect(8, 8, w - 16, h - 16)
      })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'sushi', name: 'Omakase', category: 'Asian', emoji: '🍣',
    defaultColors: { fg: '#1a1a1a', bg: '#f7f3ef', accent: '#d4291a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawMinimalJapanese(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'bbq', name: 'Smokehouse', category: 'American', emoji: '🔥',
    defaultColors: { fg: '#f5ede0', bg: '#1c1008', accent: '#d4522a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawWoodGrain(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'brunch', name: 'Brunch Club', category: 'Café', emoji: '🥞',
    defaultColors: { fg: '#3a2e2e', bg: '#fff9f5', accent: '#f4a89a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawPolkaDots(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'steakhouse', name: 'Steakhouse', category: 'American', emoji: '🥩',
    defaultColors: { fg: '#f5ede0', bg: '#1a1010', accent: '#8b0000' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        ctx.strokeStyle = colors.accent; ctx.lineWidth = 3
        ctx.strokeRect(8, 8, w - 16, h - 16)
        ctx.lineWidth = 1
        ctx.strokeRect(13, 13, w - 26, h - 26)
      })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'tapas', name: 'Tapas', category: 'Mediterranean', emoji: '🫒',
    defaultColors: { fg: '#2c1810', bg: '#fef9ec', accent: '#d4853a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawMosaicBorder(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'neon_diner', name: 'Neon Diner', category: 'American', emoji: '🎰',
    defaultColors: { fg: '#00ff88', bg: '#0d0d1a', accent: '#ff006e' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        ctx.shadowBlur = 12; ctx.shadowColor = colors.accent
        ctx.strokeStyle = colors.accent; ctx.lineWidth = 3
        roundRectPath(ctx, 8, 8, w - 16, h - 16, 10); ctx.stroke(); ctx.shadowBlur = 0
        ctx.shadowBlur = 10; ctx.shadowColor = colors.fg
        ctx.strokeStyle = colors.fg; ctx.lineWidth = 1.5
        roundRectPath(ctx, 14, 14, w - 28, h - 28, 6); ctx.stroke(); ctx.shadowBlur = 0
      })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'garden', name: 'Garden', category: 'Mediterranean', emoji: '🌿',
    defaultColors: { fg: '#1a3025', bg: '#f0f7f1', accent: '#5a9e6f' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawLeafBorder(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'cocktail', name: 'Cocktail', category: 'Bar & Lounge', emoji: '🍸',
    defaultColors: { fg: '#f5f0ff', bg: '#1a0a2e', accent: '#a855f7' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
      grad.addColorStop(0, '#1a0a2e'); grad.addColorStop(1, '#2e1a40')
      ctx.fillStyle = grad; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawSparkles(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'boulangerie', name: 'Boulangerie', category: 'Café', emoji: '🥐',
    defaultColors: { fg: '#2c1a00', bg: '#fef7ea', accent: '#d4832a' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => drawWeaveBorder(ctx, w, h, colors.accent))
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'minimal_clean', name: 'Minimal', category: 'Modern', emoji: '◻️',
    defaultColors: { fg: '#111111', bg: '#ffffff', accent: '#111111' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        ctx.fillStyle = colors.accent
        ctx.fillRect(0, 0, w, 5); ctx.fillRect(0, h - 5, w, 5)
      })
      await drawQr(canvas, qrLink, colors)
    } },
  { id: 'kraft', name: 'Kraft Paper', category: 'Modern', emoji: '📦',
    defaultColors: { fg: '#2c1a00', bg: '#d4a96a', accent: '#2c1a00' },
    render: async ({ canvas, qrLink, colors }) => {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height)
      withScaledDecor(ctx, canvas, (w, h) => {
        for (let i = 0; i < 120; i++) {
          ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.04})`
          ctx.fillRect(Math.random() * w, Math.random() * h, Math.random() * 40 + 10, 1)
        }
        ctx.strokeStyle = colors.accent + 'aa'; ctx.lineWidth = 2
        ctx.setLineDash([8, 4]); ctx.strokeRect(10, 10, w - 20, h - 20); ctx.setLineDash([])
      })
      await drawQr(canvas, qrLink, colors)
    } },
]

const CATEGORIES = ['All', ...Array.from(new Set(QR_THEMES.map((t) => t.category)))]

/* ═══════════════════════════════════════════════════════════
   Text layers
═══════════════════════════════════════════════════════════ */
let layerSeq = 0
const newLayerId = () => `layer-${Date.now()}-${layerSeq++}`

const makeDefaultLayers = (inkColor, cafeName) => ([
  {
    id: newLayerId(), text: cafeName || 'Your Restaurant Name',
    xPct: 50, yPct: 80, fontFamily: FONT_OPTIONS[0].value, fontSize: 24,
    fontWeight: 700, italic: false, uppercase: true, color: inkColor,
    bgEnabled: false, bgColor: '#000000', bgOpacity: 35, bgPaddingX: 10, bgPaddingY: 6,
    locked: true, kind: 'name',
  },
  {
    id: newLayerId(), text: 'Scan to view menu & order',
    xPct: 50, yPct: 90, fontFamily: FONT_OPTIONS[2].value, fontSize: 11,
    fontWeight: 500, italic: false, uppercase: false, color: inkColor,
    bgEnabled: false, bgColor: '#000000', bgOpacity: 35, bgPaddingX: 8, bgPaddingY: 4,
    locked: false, kind: 'tagline',
  },
])

function hexToRgba(hex, alphaPct) {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const r = parseInt(full.substring(0, 2), 16) || 0
  const g = parseInt(full.substring(2, 4), 16) || 0
  const b = parseInt(full.substring(4, 6), 16) || 0
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(100, alphaPct)) / 100})`
}

function drawTextLayer(ctx, canvas, layer) {
  const text = (layer.uppercase ? layer.text.toUpperCase() : layer.text).trim()
  if (!text) return
  const scale = Math.min(canvas.width, canvas.height) / SIZE
  const fontPx = layer.fontSize * scale
  ctx.font = `${layer.italic ? 'italic ' : ''}${layer.fontWeight} ${fontPx}px ${layer.fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const x = canvas.width * (layer.xPct / 100)
  const y = canvas.height * (layer.yPct / 100)

  if (layer.bgEnabled) {
    const metrics = ctx.measureText(text)
    const padX = layer.bgPaddingX * scale, padY = layer.bgPaddingY * scale
    const bw = metrics.width + padX * 2, bh = fontPx + padY * 2
    roundRect(ctx, x - bw / 2, y - bh / 2, bw, bh, 6 * scale, hexToRgba(layer.bgColor, layer.bgOpacity))
  }
  ctx.fillStyle = layer.color
  ctx.fillText(text, x, y)
}

/* This is only ever called for a one-shot export (gallery thumbnails
   skip it, the live editor skips it) — it bakes theme + QR + all text
   layers into a single flat canvas at whatever resolution the canvas
   is already sized to. */
async function renderComposite(canvas, { theme, qrLink, colors, layers = [] }) {
  await theme.render({ canvas, qrLink, colors })
  const ctx = canvas.getContext('2d')
  layers.forEach((layer) => drawTextLayer(ctx, canvas, layer))
}

/* ═══════════════════════════════════════════════════════════
   Small hook: measure an element's content box responsively
═══════════════════════════════════════════════════════════ */
function useElementSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const box = entry.contentBoxSize
        ? (Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize)
        : null
      if (box) setSize({ width: box.inlineSize, height: box.blockSize })
      else setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])
  return size
}

function fitContain(boxW, boxH, aspect) {
  if (!boxW || !boxH || !aspect) return { w: 0, h: 0 }
  const boxAspect = boxW / boxH
  if (aspect >= boxAspect) {
    const w = boxW
    return { w, h: w / aspect }
  }
  const h = boxH
  return { w: h * aspect, h }
}

const DPR = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 1

/* ═══════════════════════════════════════════════════════════
   Gallery card — used in the Design step
═══════════════════════════════════════════════════════════ */
const ThemeCard = React.memo(({ theme, qrLink, isSelected, onClick }) => {
  const canvasRef = useRef(null)
  const [rendered, setRendered] = useState(false)

  useEffect(() => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    canvas.width = 220 * DPR; canvas.height = 220 * DPR
    setRendered(false)
    theme.render({ canvas, qrLink, colors: theme.defaultColors })
      .then(() => setRendered(true))
      .catch(console.error)
  }, [theme, qrLink])

  return (
    <button type="button" className={`qrm-theme-card ${isSelected ? 'is-selected' : ''}`} onClick={onClick}>
      <canvas ref={canvasRef} className="qrm-theme-canvas" style={{ opacity: rendered ? 1 : 0.2 }} />
      <div className="qrm-theme-meta">
        <span className="qrm-theme-emoji" aria-hidden="true">{theme.emoji}</span>
        <span className="qrm-theme-name">{theme.name}</span>
        <span className="qrm-theme-category">{theme.category}</span>
      </div>
    </button>
  )
})

/* ═══════════════════════════════════════════════════════════
   Guidance banner — contextual hint shown above each step's
   content, swapped for a confirmation once the step's choice
   is made (paired with the highlighted Continue button below).
═══════════════════════════════════════════════════════════ */
const Hint = ({ tone = 'info', children }) => (
  <div className={`qrm-hint qrm-hint-${tone}`}>
    <span className="qrm-hint-icon" aria-hidden="true">{tone === 'done' ? '✓' : '👉'}</span>
    <span>{children}</span>
  </div>
)

/* ═══════════════════════════════════════════════════════════
   Step 1 — Design
═══════════════════════════════════════════════════════════ */
const DesignStep = ({ qrLink, selectedTheme, onSelect, onNext }) => {
  const [activeCategory, setActiveCategory] = useState('All')
  const filteredThemes = useMemo(
    () => QR_THEMES.filter((t) => activeCategory === 'All' || t.category === activeCategory),
    [activeCategory]
  )

  return (
    <div className="qrm-step">
      <div className="qrm-step-heading">
        <h2>Choose a design</h2>
        <p>Pick a base style — you'll customize colors, text and layout in the next steps.</p>
      </div>

      {selectedTheme ? (
        <Hint tone="done">{selectedTheme.emoji} {selectedTheme.name} selected — hit Continue below, or tap another design to switch.</Hint>
      ) : (
        <Hint>Tap a design card to select it.</Hint>
      )}

      <div className="qrm-pill-row">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`qrm-pill ${activeCategory === cat ? 'is-active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
        <span className="qrm-pill-count">{filteredThemes.length} designs</span>
      </div>

      <div className="qrm-theme-grid">
        {filteredThemes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            qrLink={qrLink}
            isSelected={selectedTheme?.id === theme.id}
            onClick={() => onSelect(theme)}
          />
        ))}
      </div>

      <div className="qrm-step-footer qrm-step-footer-end">
        <button
          type="button"
          className={`qrm-btn qrm-btn-primary ${selectedTheme ? 'qrm-btn-highlight' : ''}`}
          disabled={!selectedTheme}
          onClick={onNext}
        >
          Continue with {selectedTheme ? selectedTheme.name : 'a design'} →
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Step 2 — Size
═══════════════════════════════════════════════════════════ */
const SizeStep = ({ selectedSize, onSelect, onBack, onNext }) => {
  const groups = ['Print', 'Digital']
  return (
    <div className="qrm-step">
      <div className="qrm-step-heading">
        <h2>Choose a size</h2>
        <p>Pick where this QR code will be printed or shared. Every template is production-ready at the correct DPI.</p>
      </div>

      {selectedSize ? (
        <Hint tone="done">{selectedSize.icon} {selectedSize.label} selected — hit Continue below, or pick another size to switch.</Hint>
      ) : (
        <Hint>Pick a size for where you'll place this QR code.</Hint>
      )}

      <SizeCompare size={selectedSize} />

      {groups.map((group) => (
        <div key={group} className="qrm-size-group">
          <div className="qrm-size-group-label">{group}</div>
          <div className="qrm-size-grid">
            {SIZE_TEMPLATES.filter((t) => t.category === group).map((t) => (
              <button
                key={t.id}
                type="button"
                className={`qrm-size-card ${selectedSize?.id === t.id ? 'is-selected' : ''}`}
                onClick={() => onSelect(t)}
              >
                <span className="qrm-size-icon" aria-hidden="true">{t.icon}</span>
                <span className="qrm-size-label">{t.label}</span>
                <span className="qrm-size-dims">{formatDims(t)}</span>
                <span className="qrm-size-note">{t.note}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="qrm-step-footer">
        <button type="button" className="qrm-btn qrm-btn-ghost" onClick={onBack}>← Change design</button>
        <button
          type="button"
          className={`qrm-btn qrm-btn-primary ${selectedSize ? 'qrm-btn-highlight' : ''}`}
          disabled={!selectedSize}
          onClick={onNext}
        >
          Continue to customize →
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Draggable text layer — pure DOM/CSS, live during editing.
   Uses Pointer Capture so drag events always route back to this
   element (no window listeners to leak, no drift if the cursor
   leaves the element mid-drag).
═══════════════════════════════════════════════════════════ */
const DraggableLayer = ({ layer, isSelected, frameWidth, wrapperRef, onSelect, onMove }) => {
  const elRef = useRef(null)

  const handlePointerDown = (e) => {
    e.stopPropagation()
    onSelect(layer.id)
    elRef.current?.setPointerCapture(e.pointerId)
  }
  const handlePointerMove = (e) => {
    if (e.buttons === 0) return
    const wrapper = wrapperRef.current
    if (!wrapper) return
    const rect = wrapper.getBoundingClientRect()
    let xPct = ((e.clientX - rect.left) / rect.width) * 100
    let yPct = ((e.clientY - rect.top) / rect.height) * 100
    xPct = Math.min(97, Math.max(3, xPct))
    yPct = Math.min(98, Math.max(2, yPct))
    onMove(layer.id, xPct, yPct)
  }
  const handlePointerUp = (e) => {
    try { elRef.current?.releasePointerCapture(e.pointerId) } catch { /* already released */ }
  }

  const fontSizePx = frameWidth ? (layer.fontSize / SIZE) * frameWidth : 0

  return (
    <div
      ref={elRef}
      className={`qrm-drag-layer ${isSelected ? 'is-selected' : ''}`}
      style={{
        left: `${layer.xPct}%`,
        top: `${layer.yPct}%`,
        color: layer.color,
        fontFamily: layer.fontFamily,
        fontWeight: layer.fontWeight,
        fontStyle: layer.italic ? 'italic' : 'normal',
        fontSize: `${fontSizePx}px`,
        background: layer.bgEnabled ? hexToRgba(layer.bgColor, layer.bgOpacity) : 'transparent',
        padding: layer.bgEnabled
          ? `${(layer.bgPaddingY / SIZE) * frameWidth}px ${(layer.bgPaddingX / SIZE) * frameWidth}px`
          : 0,
        textTransform: layer.uppercase ? 'uppercase' : 'none',
        zIndex: isSelected ? 5 : 2,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {layer.text || '\u00A0'}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Live editor stage — fixed square workspace (never resizes
   when the template changes). The artwork inside is fit to the
   template's true aspect ratio and rendered at native (HiDPI)
   resolution so lines and QR modules stay sharp. The canvas here
   holds ONLY the theme background + QR — never text — so nothing
   is ever drawn twice and dragging never triggers a re-render.
═══════════════════════════════════════════════════════════ */
const EditorCanvas = ({ theme, size, qrLink, colors, layers, selectedLayerId, onSelectLayer, onMoveLayer }) => {
  const stageRef = useRef(null)
  const frameRef = useRef(null)
  const canvasRef = useRef(null)
  const stageBox = useElementSize(stageRef)

  const px = useMemo(() => getTemplatePx(size), [size])
  const aspect = px.w / px.h

  const STAGE_PADDING = 24
  const innerW = Math.max(0, stageBox.width - STAGE_PADDING * 2)
  const innerH = Math.max(0, stageBox.height - STAGE_PADDING * 2)
  const frame = fitContain(innerW, innerH, aspect)

  useEffect(() => {
    if (!canvasRef.current || frame.w < 2 || frame.h < 2) return
    const canvas = canvasRef.current
    canvas.width = Math.round(frame.w * DPR)
    canvas.height = Math.round(frame.h * DPR)
    theme.render({ canvas, qrLink, colors }).catch(console.error)
  }, [theme, qrLink, colors, frame.w, frame.h])

  return (
    <div ref={stageRef} className="qrm-stage">
      {frame.w > 2 && frame.h > 2 && (
        <div
          ref={frameRef}
          className="qrm-canvas-frame"
          style={{ width: frame.w, height: frame.h }}
          onPointerDown={() => onSelectLayer(null)}
        >
          <canvas
            ref={canvasRef}
            className="qrm-editor-canvas"
            style={{ width: frame.w, height: frame.h }}
          />
          {layers.map((layer) => (
            <DraggableLayer
              key={layer.id}
              layer={layer}
              isSelected={selectedLayerId === layer.id}
              frameWidth={frame.w}
              wrapperRef={frameRef}
              onSelect={onSelectLayer}
              onMove={onMoveLayer}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Layer property panel
═══════════════════════════════════════════════════════════ */
const LayerPanel = ({ layer, onChange, onDelete }) => {
  if (!layer) {
    return (
      <div className="qrm-panel-empty">
        Select a text layer on the canvas — or add a new one — to edit it here.
      </div>
    )
  }

  const set = (patch) => onChange(layer.id, patch)

  return (
    <div className="qrm-layer-panel">
      <label className="qrm-field">
        <span>Text</span>
        <input type="text" value={layer.text} maxLength={40} onChange={(e) => set({ text: e.target.value })} />
      </label>

      <label className="qrm-field">
        <span>Font</span>
        <select value={layer.fontFamily} onChange={(e) => set({ fontFamily: e.target.value })}>
          {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </label>

      <div className="qrm-field-row">
        <label className="qrm-field">
          <span>Weight</span>
          <select value={layer.fontWeight} onChange={(e) => set({ fontWeight: Number(e.target.value) })}>
            {WEIGHT_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </label>
        <label className="qrm-field qrm-field-narrow">
          <span>Size</span>
          <input type="range" min="8" max="60" value={layer.fontSize} onChange={(e) => set({ fontSize: Number(e.target.value) })} />
        </label>
      </div>

      <div className="qrm-toggle-row">
        <label className="qrm-toggle">
          <input type="checkbox" checked={layer.italic} onChange={(e) => set({ italic: e.target.checked })} />
          <span>Italic</span>
        </label>
        <label className="qrm-toggle">
          <input type="checkbox" checked={layer.uppercase} onChange={(e) => set({ uppercase: e.target.checked })} />
          <span>Uppercase</span>
        </label>
      </div>

      <div className="qrm-field-row">
        <label className="qrm-field qrm-field-narrow">
          <span>Text color</span>
          <input type="color" value={layer.color} onChange={(e) => set({ color: e.target.value })} />
        </label>
        <label className="qrm-toggle" style={{ alignSelf: 'flex-end', marginBottom: 6 }}>
          <input type="checkbox" checked={layer.bgEnabled} onChange={(e) => set({ bgEnabled: e.target.checked })} />
          <span>Background</span>
        </label>
      </div>

      {layer.bgEnabled && (
        <div className="qrm-field-row">
          <label className="qrm-field qrm-field-narrow">
            <span>BG color</span>
            <input type="color" value={layer.bgColor} onChange={(e) => set({ bgColor: e.target.value })} />
          </label>
          <label className="qrm-field">
            <span>Opacity</span>
            <input type="range" min="0" max="100" value={layer.bgOpacity} onChange={(e) => set({ bgOpacity: Number(e.target.value) })} />
          </label>
        </div>
      )}

      {!layer.locked && (
        <button type="button" className="qrm-btn qrm-btn-danger-outline" onClick={() => onDelete(layer.id)}>
          Delete this layer
        </button>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Step 3 — Editor
═══════════════════════════════════════════════════════════ */
const EditorStep = ({
  theme, size, qrLink, colors, setColors, layers, setLayers,
  onBackToDesign, onBackToSize, selectedLayerId, setSelectedLayerId,
}) => {
  const [panelTab, setPanelTab] = useState('layers')
  const selectedLayer = layers.find((l) => l.id === selectedLayerId) || null
  const px = getTemplatePx(size)

  const updateLayer = useCallback((id, patch) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }, [setLayers])

  const moveLayer = useCallback((id, xPct, yPct) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, xPct, yPct } : l)))
  }, [setLayers])

  const addLayer = () => {
    if (layers.length >= 4) return
    const id = newLayerId()
    const layer = {
      id, text: 'Add details…', xPct: 50, yPct: 60, fontFamily: FONT_OPTIONS[2].value, fontSize: 12,
      fontWeight: 500, italic: false, uppercase: false, color: colors.fg,
      bgEnabled: false, bgColor: '#000000', bgOpacity: 35, bgPaddingX: 8, bgPaddingY: 4,
      locked: false, kind: 'extra',
    }
    setLayers((prev) => [...prev, layer])
    setSelectedLayerId(id)
    setPanelTab('layers')
  }

  const deleteLayer = (id) => {
    setLayers((prev) => prev.filter((l) => l.id !== id))
    setSelectedLayerId(null)
  }

  const resetColors = () => setColors(theme.defaultColors)

  return (
    <div className="qrm-step qrm-editor-step">
      <div className="qrm-breadcrumbs">
        <button type="button" onClick={onBackToDesign}>{theme.emoji} {theme.name}</button>
        <span>/</span>
        <button type="button" onClick={onBackToSize}>{size.icon} {size.label}</button>
      </div>

      <Hint>Drag any text directly on the canvas to reposition it. Use the panel on the right to change fonts, colors and backgrounds.</Hint>

      <div className="qrm-editor-layout">
        <div className="qrm-editor-main">
          <EditorCanvas
            theme={theme}
            size={size}
            qrLink={qrLink}
            colors={colors}
            layers={layers}
            selectedLayerId={selectedLayerId}
            onSelectLayer={setSelectedLayerId}
            onMoveLayer={moveLayer}
          />
          <div className="qrm-spec-strip">
            <span>{formatDims(size)}</span>
            <span className="qrm-spec-sep">·</span>
            <span>{px.w} × {px.h}px</span>
            <span className="qrm-spec-sep">·</span>
            <span>{size.dpi} DPI</span>
          </div>
        </div>

        <div className="qrm-editor-side">
          <div className="qrm-tab-row">
            <button type="button" className={`qrm-tab ${panelTab === 'layers' ? 'is-active' : ''}`} onClick={() => setPanelTab('layers')}>Text</button>
            <button type="button" className={`qrm-tab ${panelTab === 'colors' ? 'is-active' : ''}`} onClick={() => setPanelTab('colors')}>Colors</button>
          </div>

          {panelTab === 'layers' ? (
            <>
              <div className="qrm-layer-list">
                {layers.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    className={`qrm-layer-chip ${selectedLayerId === l.id ? 'is-active' : ''}`}
                    onClick={() => setSelectedLayerId(l.id)}
                  >
                    {l.text || 'Untitled'}
                  </button>
                ))}
                <button type="button" className="qrm-layer-chip qrm-layer-chip-add" onClick={addLayer} disabled={layers.length >= 4}>
                  + Add text
                </button>
              </div>
              <LayerPanel layer={selectedLayer} onChange={updateLayer} onDelete={deleteLayer} />
            </>
          ) : (
            <div className="qrm-colors-panel">
              <div className="qrm-field-row qrm-colors-row">
                <label className="qrm-field qrm-field-narrow">
                  <span>Ink</span>
                  <input type="color" value={colors.fg} onChange={(e) => setColors((c) => ({ ...c, fg: e.target.value }))} />
                </label>
                <label className="qrm-field qrm-field-narrow">
                  <span>Background</span>
                  <input type="color" value={colors.bg} onChange={(e) => setColors((c) => ({ ...c, bg: e.target.value }))} />
                </label>
                <label className="qrm-field qrm-field-narrow">
                  <span>Accent</span>
                  <input type="color" value={colors.accent} onChange={(e) => setColors((c) => ({ ...c, accent: e.target.value }))} />
                </label>
              </div>
              <button type="button" className="qrm-btn qrm-btn-ghost qrm-btn-full" onClick={resetColors}>
                Reset to theme defaults
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Download modal — the ONLY place text gets baked into a
   canvas, rendered once at true print resolution.
═══════════════════════════════════════════════════════════ */
const DownloadModal = ({ theme, size, qrLink, colors, layers, onClose }) => {
  const canvasRef = useRef(null)
  const [rendered, setRendered] = useState(false)
  const px = getTemplatePx(size)

  useEffect(() => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    canvas.width = px.w; canvas.height = px.h
    setRendered(false)
    renderComposite(canvas, { theme, qrLink, colors, layers })
      .then(() => setRendered(true))
      .catch(console.error)
  }, [theme, qrLink, colors, layers, px.w, px.h])

  const handleDownload = () => {
    if (!canvasRef.current || !rendered) return
    const link = document.createElement('a')
    link.download = `qr-${theme.id}-${size.id}-${px.w}x${px.h}.png`
    link.href = canvasRef.current.toDataURL('image/png')
    link.click()
  }

  return (
    <div className="qrm-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="qrm-modal">
        <div className="qrm-modal-header">
          <div>
            <div className="qrm-modal-title">{theme.emoji} {theme.name} — {size.label}</div>
            <div className="qrm-modal-subtitle">Ready to print & place on your tables</div>
          </div>
          <button type="button" className="qrm-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="qrm-modal-canvas-wrap">
          <canvas ref={canvasRef} className="qrm-modal-canvas" style={{ opacity: rendered ? 1 : 0.35 }} />
        </div>

        <div className="qrm-modal-specs">{formatDims(size)}</div>

        <button type="button" className="qrm-btn qrm-btn-primary qrm-btn-full" onClick={handleDownload} disabled={!rendered}>
          {rendered ? `⬇ Download PNG — ${px.w} × ${px.h}px` : 'Rendering…'}
        </button>

        <div className="qrm-modal-tip">
          💡 <strong>Tip:</strong> Print at the exact size shown above for the sharpest scan. Digital sizes are ready to share as-is.
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Step indicator (compact — lives in the fixed top bar)
═══════════════════════════════════════════════════════════ */
const STEPS = [
  { key: 'design', label: 'Design' },
  { key: 'size', label: 'Size' },
  { key: 'editor', label: 'Customize' },
]

const StepIndicator = ({ current, furthestUnlocked, onJump }) => {
  const currentIdx = STEPS.findIndex((s) => s.key === current)
  return (
    <div className="qrm-topbar-steps">
      {STEPS.map((s, i) => {
        const unlocked = i <= furthestUnlocked
        return (
          <React.Fragment key={s.key}>
            <button
              type="button"
              className={`qrm-step-dot ${i === currentIdx ? 'is-current' : ''} ${i < currentIdx ? 'is-done' : ''}`}
              disabled={!unlocked}
              onClick={() => unlocked && onJump(s.key)}
            >
              <span className="qrm-step-num">{i + 1}</span>
              <span className="qrm-step-label">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <span className="qrm-step-connector" />}
          </React.Fragment>
        )
      })}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   Main component
═══════════════════════════════════════════════════════════ */
const TableQRManager = ({ cafeid, cafeName }) => {
  const qrLink = `${clienturl}orderpage/${cafeid}`

  const [step, setStep] = useState('design')
  const [selectedTheme, setSelectedTheme] = useState(null)
  const [selectedSize, setSelectedSize] = useState(null)
  const [colors, setColors] = useState({})
  const [layers, setLayers] = useState([])
  const [selectedLayerId, setSelectedLayerId] = useState(null)
  const [showDownload, setShowDownload] = useState(false)

  const furthestUnlocked = selectedSize ? 2 : selectedTheme ? 1 : 0

  const handleSelectTheme = (theme) => {
    setSelectedTheme(theme)
    setColors(theme.defaultColors)
  }

  const goToSize = () => setStep('size')
  const goToEditor = () => {
    if (layers.length === 0) {
      const initial = makeDefaultLayers(selectedTheme.defaultColors.fg, cafeName)
      setLayers(initial)
      setSelectedLayerId(initial[0].id)
    }
    setStep('editor')
  }

  const effectiveColors = selectedTheme ? { ...selectedTheme.defaultColors, ...colors } : {}

  return (
    <div className="qrm-root">
      <div className="qrm-topbar">
        <div className="qrm-topbar-brand">🔗 <strong>QR Studio</strong></div>
        <StepIndicator current={step} furthestUnlocked={furthestUnlocked} onJump={setStep} />
        <div className="qrm-topbar-actions">
          {step === 'editor' && selectedTheme && selectedSize && (
            <button type="button" className="qrm-btn qrm-btn-primary qrm-btn-sm" onClick={() => setShowDownload(true)}>
              ⬇ Download
            </button>
          )}
        </div>
      </div>

      <div className="qrm-body">
        {step === 'design' && (
          <DesignStep
            qrLink={qrLink}
            selectedTheme={selectedTheme}
            onSelect={handleSelectTheme}
            onNext={goToSize}
          />
        )}

        {step === 'size' && selectedTheme && (
          <SizeStep
            selectedSize={selectedSize}
            onSelect={setSelectedSize}
            onBack={() => setStep('design')}
            onNext={goToEditor}
          />
        )}

        {step === 'editor' && selectedTheme && selectedSize && (
          <EditorStep
            theme={selectedTheme}
            size={selectedSize}
            qrLink={qrLink}
            colors={effectiveColors}
            setColors={setColors}
            layers={layers}
            setLayers={setLayers}
            selectedLayerId={selectedLayerId}
            setSelectedLayerId={setSelectedLayerId}
            onBackToDesign={() => setStep('design')}
            onBackToSize={() => setStep('size')}
          />
        )}
      </div>

      {showDownload && selectedTheme && selectedSize && (
        <DownloadModal
          theme={selectedTheme}
          size={selectedSize}
          qrLink={qrLink}
          colors={effectiveColors}
          layers={layers}
          onClose={() => setShowDownload(false)}
        />
      )}
    </div>
  )
}

export default TableQRManager